import {
  Button,
  SearchBox,
  OutlinedSelect,
  SelectOptionType,
} from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { StudentsRemovalRequestHistoryFiltersResponse } from '../types/history';
import { useStudentsRemovalRequestHistoryList } from './StudentsRemovalRequestHistoryListLoader';

interface StudentsRemovalRequestHistoryFiltersForm {
  studentNameOrId: string;
  studyPlanVersion: SelectOptionType | null;
  requestState: SelectOptionType | null;
}

export function StudentsRemovalRequestHistoryFilters({
  data,
}: {
  data: StudentsRemovalRequestHistoryFiltersResponse;
}) {
  const { updateQuery } = useStudentsRemovalRequestHistoryList();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_MANAGEMENT_STUDENT_REMOVAL_REQUEST_HISTORY,
  );
  const texts = useMemo(() => {
    return t<
      string,
      {
        student: string;
        studentPlaceholder: string;
        studyPlanVersionName: string;
        studyPlanVersionNamePlaceholder: string;
        requestState: string;
        requestStatePlaceholder: string;
        search: string;
        clean: string;
      }
    >('history.filters', { returnObjects: true });
  }, [t]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
  } = useForm<StudentsRemovalRequestHistoryFiltersForm>({
    defaultValues: {
      studentNameOrId: '',
      studyPlanVersion: null,
      requestState: null,
    },
  });

  const { validateMinLength } = useValidations();

  const localSubmit = useCallback(
    (values: StudentsRemovalRequestHistoryFiltersForm) => {
      console.log({ values });
      updateQuery({
        page: 1,
        studentNameOrId: values.studentNameOrId,
        studyPlanVersionId:
          typeof values.studyPlanVersion?.value === 'number'
            ? values.studyPlanVersion?.value
            : undefined,
        requestState:
          typeof values.requestState?.value === 'string'
            ? values.requestState.value
            : undefined,
      });
    },
    [updateQuery],
  );

  const studyPlanVersionsOptions = useMemo(() => {
    return data.studyPlanVersions.map((s) => ({
      value: s.id,
      label: s.name,
    }));
  }, [data.studyPlanVersions]);
  const statesOptions = useMemo(() => {
    return data.states.map((s) => ({
      value: s.id,
      label: s.name,
    }));
  }, [data.states]);

  return (
    <form onSubmit={handleSubmit(localSubmit)}>
      <Row>
        <Col xs={12} md={4}>
          <SearchBox
            name="studentNameOrId"
            label={texts.student}
            placeholder={texts.studentPlaceholder}
            control={control}
            clear={() => setValue('studentNameOrId', '')}
            minLength={3}
            rules={{
              validate: {
                minLength: validateMinLength(3),
              },
            }}
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            control={control}
            name="studyPlanVersion"
            label={texts.studyPlanVersionName}
            placeholder={texts.studyPlanVersionNamePlaceholder}
            options={studyPlanVersionsOptions}
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            control={control}
            name="requestState"
            label={texts.requestState}
            placeholder={texts.requestStatePlaceholder}
            options={statesOptions}
          />
        </Col>
        <Col xs={12} md={4} className="ml-auto">
          <div className="d-flex justify-content-end mt-4">
            <Button
              type="submit"
              color="primary"
              className="mb-3"
              size="md"
              text={texts.search}
            />
            <Button
              type="submit"
              color="primary"
              outlined
              className="mb-3 ml-2"
              onClick={() => {
                reset();
              }}
              size="md"
              text={texts.clean}
            />
          </div>
        </Col>
      </Row>
    </form>
  );
}
